import * as shared from "@/styles/shared";
import { MeterSubmissionKind } from "@shared/types/meter";
import { RefreshCw, Upload } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import * as styles from "../MeterReadingsPage.styles";
import { CONFIDENCE_BADGE, CONFIDENCE_KEYS } from "./badges";
import type { CaptureCardProps } from "./types";

const CaptureCard = ({ provider, capture, actions, settingsIncomplete }: CaptureCardProps) => {
  const { t } = useTranslation(["common", "meterReadings"]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const usesEmail = provider.submission === MeterSubmissionKind.EMAIL;
  const confirmLabel = usesEmail ? t("meterReadings:review.confirmEmail") : t("meterReadings:review.confirmForm");

  return (
    <div className={styles.card}>
      <h2 className={styles.cardTitle}>{provider.name}</h2>

      {settingsIncomplete && <p className={styles.warning}>{t("meterReadings:settings.missingWarning")}</p>}

      {capture.extracting && (
        <div className={styles.uploadArea}>
          <RefreshCw className={styles.uploadingIcon} />
          <p className={shared.titleSm}>{t("meterReadings:extracting")}</p>
        </div>
      )}

      {!capture.extracting && !capture.extraction && (
        <div className={styles.uploadArea} onClick={() => fileInputRef.current?.click()}>
          <Upload className={styles.uploadIcon} />
          <p className={shared.titleSm}>{t("meterReadings:action.takePhoto")}</p>
          <p className={shared.mutedXs}>{t("meterReadings:uploadHint")}</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              void actions.selectFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </div>
      )}

      {capture.extraction && (
        <div className={styles.reviewGrid}>
          <div className={styles.fieldGroup}>
            <label className={shared.titleSm}>{t("meterReadings:review.readingValue")}</label>
            <input value={capture.readingValue} onChange={(e) => actions.changeReading(e.target.value)} className={styles.input} />
            {capture.usesSecondaryReading && (
              <>
                <label className={shared.titleSm}>{t("meterReadings:review.secondaryReadingValue")}</label>
                <input value={capture.secondaryReadingValue} onChange={(e) => actions.changeSecondary(e.target.value)} className={styles.input} />
              </>
            )}
            <p className={shared.mutedXs}>{t("meterReadings:review.editHint")}</p>

            <div className={styles.badgeRow}>
              {capture.extraction.confidence && (
                <span className={CONFIDENCE_BADGE[capture.extraction.confidence]}>
                  {t("meterReadings:review.confidence")}: {t(CONFIDENCE_KEYS[capture.extraction.confidence])}
                </span>
              )}
            </div>

            <div className={styles.actionRow}>
              <button
                onClick={() => void actions.confirm()}
                disabled={capture.confirming || !capture.readingValue.trim()}
                className={styles.primaryButton}
              >
                {capture.confirming && <RefreshCw className={styles.spinner} />}
                {capture.confirming ? t("meterReadings:processing") : confirmLabel}
              </button>
              <button onClick={actions.retake} disabled={capture.confirming} className={styles.secondaryButton}>
                {t("meterReadings:review.retake")}
              </button>
            </div>
          </div>

          <div className={styles.fieldGroup}>
            {capture.photoDataUrl && <img src={capture.photoDataUrl} alt={t("meterReadings:photoAlt")} className={styles.photoPreview} />}
            {capture.extraction.meterNumber && (
              <p className={styles.valueText}>
                {t("meterReadings:review.meterNumber")}: {capture.extraction.meterNumber}
              </p>
            )}
            {capture.extraction.rawText && (
              <p className={styles.valueText}>
                {t("meterReadings:review.rawText")}: {capture.extraction.rawText}
              </p>
            )}
            {capture.extraction.notes && (
              <p className={styles.valueText}>
                {t("meterReadings:review.notes")}: {capture.extraction.notes}
              </p>
            )}
          </div>
        </div>
      )}

      {capture.error && <p className={shared.errorText}>{capture.error}</p>}
      {capture.successMessage && <p className={styles.success}>{capture.successMessage}</p>}
    </div>
  );
};

export default CaptureCard;
